import {effect, Injectable, signal} from '@angular/core';
import {Utils} from '@/classes/utils';
import {TactaCanvas} from '@/_services/tacta-card.service';
import {GLOBALS} from '@/_services/globals.service';

export class QuickGame {
  suite: number;
  score: number;
  active: boolean;
}

export class Player {
  playerIdx: number;
  name: string;
  color: number;
  cards: number[] = [];
  score = 0;
  sabotage = 'os-';
  //quick = '0**1--2--'; // xss - x=suite, ss=score --=no game **=current
  quick: QuickGame[] = [
    {suite: 0, score: -1, active: true},
    {suite: 1, score: -1, active: false},
    {suite: 2, score: -1, active: false}
  ];
  cvs: { [key: number]: TactaCanvas } = {};
}

export class CardConfig {
  static defWidth = 63;
  static defHeight = 89;
  static defBorder = 1.5;
  colors: { s: string, f: string, sm: string, fm: string, cm: string, dm: string, t: string, er: string };
  suite: number;
  cardId: number;
  flipped = false;
  // linkedCards: { card: CardConfig, link: any }[] = [];

  _areas: string;

  get areas(): string {
    if (!this.flipped) {
      return this._areas ?? '';
    }
    let ret = '';
    for (let i = 0; i < this._areas.length; i += 2) {
      ret += this.flipArea(this._areas[i]) + this._areas[i + 1];
    }
    return ret;
  }

  set areas(value: string) {
    this._areas = value;
  }

  flipArea(area: string) {
    const flips = 'badcfehgilkjmponrq';
    return flips[area.charCodeAt(0) - 97];
  }
}

export enum BoardMode {
  Prepare,
  Play,
}

export enum GameMode {
  Normal,
  Sabotage,
  Quick
}

export enum PlayerMode {
  SelectCard,
  PlaceCard,
}

@Injectable({
  providedIn: 'root'
})
export class TactaService {
  // index of the first card for selection of sabotage suite in deck
  static suiteIdx = 1000;
  boardCanvas = signal<TactaCanvas>(null);
  currentArea: TactaCanvas;
  boardMode = signal<BoardMode>(BoardMode.Prepare);
  gameMode = signal<GameMode>(GameMode.Normal);
  playerMode = signal<PlayerMode>(PlayerMode.SelectCard);
  refresh = signal<boolean>(false);
  colors: any[] = [
    {id: 0, s: '#ffffff', f: '#000000', sm: '#aaaaaa', fm: '#ffffff', cm: '#000000', dm: '#555555', t: '#ffffff', er: '#ff0000'},
    {id: 1, s: '#ffffff', f: '#e9b02f', sm: '#e9b02f', fm: '#ffffff', cm: '#e9b02f', dm: '#555555', t: '#000000', er: '#ff0000'},
    {id: 2, s: '#ffffff', f: '#d8542e', sm: '#d8542e', fm: '#ffffff', cm: '#d8542e', dm: '#555555', t: '#ffffff', er: '#ff0000'},
    {id: 3, s: '#ffffff', f: '#d64c92', sm: '#d64c92', fm: '#ffffff', cm: '#d64c92', dm: '#555555', t: '#ffffff', er: '#ff0000'},
    {id: 4, s: '#ffffff', f: '#625ca3', sm: '#625ca3', fm: '#ffffff', cm: '#625ca3', dm: '#555555', t: '#ffffff', er: '#ff0000'},
    {id: 5, s: '#ffffff', f: '#649bce', sm: '#649bce', fm: '#ffffff', cm: '#649bce', dm: '#555555', t: '#000000', er: '#ff0000'},
    {id: 6, s: '#ffffff', f: '#7bae42', sm: '#7bae42', fm: '#ffffff', cm: '#7bae42', dm: '#555555', t: '#000000', er: '#ff0000'},
  ];
  deck: { [key: number]: CardConfig };
  board: TactaCanvas[];
  players: Player[];
  playerIdx: number = 0;
  connectors: any = {
    // dbg: {key: 'rq', card: '71', scale: 5},
    ai: {x: 0, y: -22, deg: -135},
    aj: {x: 11, y: 0, deg: -45},
    ak: {x: 0, y: 22, deg: 45},
    al: {x: -11, y: 0, deg: 135},
    bi: {x: 71, y: -72, deg: 135},
    bj: {x: 82, y: 50, deg: -135},
    bk: {x: -71, y: 72, deg: -45},
    bl: {x: -82, y: -50, deg: 45},
    ci: {x: 29, y: -143, deg: 45},
    cj: {x: 181, y: 20, deg: 135},
    ck: {x: -29, y: 143, deg: 225},
    cl: {x: -181, y: -20, deg: -45},
    di: {x: -98, y: -93, deg: -45},
    dj: {x: 110, y: -71, deg: 45},
    dk: {x: 98, y: 93, deg: 135},
    dl: {x: -110, y: 71, deg: -135},
    ee: {x: 3, y: -13, deg: 180},
    ef: {x: -2, y: -13, deg: -90},
    eg: {x: -2, y: 13, deg: 0},
    eh: {x: 2, y: 13, deg: 90},
    fe: {x: 3, y: -84, deg: 90},
    ff: {x: 98, y: -13, deg: 180},
    fg: {x: -2, y: 84, deg: -90},
    fh: {x: -98, y: 13, deg: 0},
    ge: {x: -97, y: -113, deg: 0},
    gf: {x: 139, y: -84, deg: 90},
    gg: {x: 97, y: 113, deg: 180},
    gh: {x: -139, y: 84, deg: -90},
    he: {x: -138, y: -13, deg: -90},
    hf: {x: -3, y: -113, deg: 0},
    hg: {x: 138, y: 13, deg: 90},
    hh: {x: 3, y: 113, deg: 180},
    ia: {x: 13, y: -55, deg: -225},
    ib: {x: 58, y: -5, deg: 225},
    ic: {x: -13, y: 55, deg: -45},
    id: {x: -58, y: 5, deg: 45},
    ja: {x: -43, y: -116, deg: 45},
    jb: {x: 143, y: -45, deg: 135},
    jc: {x: 43, y: 116, deg: -135},
    jd: {x: -143, y: 45, deg: -45},
    ka: {x: -157, y: -76, deg: -45},
    kb: {x: 87, y: -126, deg: 45},
    kc: {x: 157, y: 76, deg: 135},
    kd: {x: -87, y: 126, deg: -135},
    la: {x: -72, y: 5, deg: 225},
    lb: {x: -28, y: -66, deg: -45},
    lc: {x: 72, y: -5, deg: 45},
    ld: {x: 28, y: 66, deg: -225},
    mm: {x: 50, y: -30, deg: 180},
    mn: {x: 22, y: 35, deg: 270},
    mo: {x: -50, y: 30, deg: 0},
    mp: {x: -22, y: -35, deg: 90},
    nm: {x: 71, y: -101, deg: 90},
    no: {x: -71, y: 101, deg: -90},
    om: {x: -50, y: -130, deg: 0},
    on: {x: 164, y: -36, deg: 90},
    oo: {x: 50, y: 130, deg: 180},
    op: {x: -164, y: 36, deg: -90},
    pm: {x: -71, y: -30, deg: -90},
    po: {x: 71, y: 30, deg: 90},
    qq: {x: 122, y: 50, deg: 180},
    qr: {x: -122, y: -50, deg: 0},
    rq: {x: 22, y: -50, deg: 0},
    rr: {x: -22, y: 50, deg: 180}
  };
  // cvsList: TactaCanvas[] = [];
  // global signal to refresh all instances of TactaCard
  markedCanvas = signal<TactaCanvas>(null);
  boardPattern: CanvasPattern;
  boardWidth: number;

  // get gamesAsString(): string {
  //   return this.games?.reduce((a, b) => a + b.asString, '') ?? '';
  // }
  boardHeight: number;
  mayConfirm = signal<boolean>(true);

  constructor() {
    effect(() => {
      GLOBALS._appData();
      if (this.players?.length > 0) {
        this.players[0].name = GLOBALS.appData?.person?.firstname;
      }
    });
  }

  get playerList() {
    if (this.players.length === 0) {
      return [];
    }
    const ret: Player[] = [];
    if (this.boardMode() === BoardMode.Play && this.players[this.playerIdx] != null) {
      ret.push(this.players[this.playerIdx]);
    }
    for (let i = 0; i < this.players.length; i++) {
      if (this.boardMode() === BoardMode.Prepare || i !== this.playerIdx) {
        ret.push(this.players[i]);
      }
    }
    return ret;
  }

  get scoreType() {
    let ret = 0;
    if (GLOBALS.isDebug) {
      ret = 1;
    }
    return ret;
  }

  cardList(player: Player) {
    let ret = player?.cards;
    if (this.boardMode() === BoardMode.Prepare) {
      ret = Array.from({length: 18}, (_, i) => i + (player?.color - 1) * 18 + 1);
    }
    return ret;
  }

  suiteOfPlayer(player: Player, idx: number, scale = 2) {
    const id = TactaService.suiteIdx + (player.color - 1) * 3 + idx;
    if (player.cvs[id] == null || player.cvs[id].cardIdx !== id) {
      player.cvs[id] = new TactaCanvas({cardIdx: id, turnIdx: -1, playerIdx: player.playerIdx, scale: scale}, this);
    }
    return player.cvs[id];
  }

  cardOfPlayer(player: Player, id: number, idx: number, scale = 2) {
    const cardIdx = this.cardList(player)?.[idx];
    if (player.cvs[id] == null || player.cvs[id].cardIdx !== cardIdx) {
      player.cvs[id] = new TactaCanvas({
        cardIdx: cardIdx,
        turnIdx: -1,
        scale: scale,
        playerIdx: player.playerIdx
      }, this);
    }
    return player.cvs[id];
  }

  card(data: any) {
    const ret = new CardConfig();
    ret.colors = this.colors[data.color ?? 0];
    ret.suite = data.suite ?? 0;
    ret.areas = data.areas ?? 'a-b-c-d-e-f-g-h-i-j-k-l-m-n-o-p-q-r-';
    ret.cardId = data.cardId ?? Object.keys(this.deck).length;
    return ret;
  }

  addToDeck(src: any, idx?: number) {
    idx ??= Object.keys(this.deck).length;
    this.deck[idx] = this.card({...src, cardId: idx});
  }

  // createTactaCanvas(src: any) {
  //   const ret = new TactaCanvas(src, this);
  //   this.cvsList.push(ret);
  //   return ret;
  // }

  createDeck() {
    this.deck = {};
    this.addToDeck({color: 0, suite: 4});
    for (let i = 1; i <= 6; i++) {
      this.addToDeck({color: i, suite: 0, areas: 'l1f0o0'});
      this.addToDeck({color: i, suite: 0, areas: 'd1j1e0'});
      this.addToDeck({color: i, suite: 0, areas: 'b0l0o3'});
      this.addToDeck({color: i, suite: 0, areas: 'a0d0q4'});
      this.addToDeck({color: i, suite: 0, areas: 'c1i1p3'});
      this.addToDeck({color: i, suite: 0, areas: 'i1j1l1o3'});
      this.addToDeck({color: i, suite: 3, areas: 'd1e0n0'});
      this.addToDeck({color: i, suite: 3, areas: 'd1i1j0'});
      this.addToDeck({color: i, suite: 3, areas: 'b0k0p3'});
      this.addToDeck({color: i, suite: 3, areas: 'b1l0o3'});
      this.addToDeck({color: i, suite: 3, areas: 'j1r4'});
      this.addToDeck({color: i, suite: 3, areas: 'a1d1q4'});
      this.addToDeck({color: i, suite: 4, areas: 'b1c0r0'});
      this.addToDeck({color: i, suite: 4, areas: 'f2l0o0'});
      this.addToDeck({color: i, suite: 4, areas: 'c1f2l0'});
      this.addToDeck({color: i, suite: 4, areas: 'd0i1n3'});
      this.addToDeck({color: i, suite: 4, areas: 'e2j0o3'});
      this.addToDeck({color: i, suite: 4, areas: 'd0e2q4'});
    }
    this.addToDeck({color: 0, suite: 0, areas: 'a0b0c0d0'});
    this.addToDeck({color: 0, suite: 0, areas: 'e0f0g0h0'});
    this.addToDeck({color: 0, suite: 3, areas: 'i0j0k0l0'});
    this.addToDeck({color: 0, suite: 4, areas: 'm0n0o0p0'});
    this.addToDeck({color: 0, suite: 4, areas: 'q0r0'});
    // cards for selection of sabotage suit
    for (let i = 0; i < 6; i++) {
      this.addToDeck({color: i + 1, suite: 0, areas: 'm9o9'}, TactaService.suiteIdx + i * 3);
      this.addToDeck({color: i + 1, suite: 3, areas: 'm9o9'}, TactaService.suiteIdx + i * 3 + 1);
      this.addToDeck({color: i + 1, suite: 4, areas: 'm9o9'}, TactaService.suiteIdx + i * 3 + 2);
    }
    let src = '';
    let idx = 0;
    for (let i = 0; i < Object.keys(this.deck).length; i++) {
      src += `${Utils.hex(i)}${Utils.hex(idx)}${Utils.hex(this.playerFromCardIdx(idx))}--[]`;
      idx++;
    }
    // src = '6Spieler 1[]5Spieler 2[55]@0000--[6201ic[]5a02qr[5b05fe[5006dl[690bci[4c10al[6615qq[6017ia[4b18oo[]]]4d16id[]]]]4e12oo[6419bl[4f1adl[5d1bon[df1dib[491eon[5720ff[]]]]]591coo[]]5c21di[4a22dj[]]]]]6c03ef[5404qq[6807la[5208oo[6709bl[de13rr[5114kb[6b1fop[]]]]]560aff[580cno[e30dai[6111dk[]]530ejd[650frr[]]]]]]6a23id[]]]';
    // src = '2Spieler 1[]5Spieler 2[49]1Spieler 3[10]@0000--[1301ff[0303oo[d105al[550ebk[d311lc[2431qq[]]cc32rr[]]1534on[]]]8506dl[4b08bi[520bbl[5414al[0b15rq[0c18aj[1d1crq[]581did[0627on[1428di[]]]]]]4e17oo[cd23di[5929on[572cfe[]]]]]1c16oo[9b22kb[]]]180aon[890cai[230don[]0f1eck[201fff[0124oo[1f25bl[]]]5a26dl[962brq[082dib[502fdj[1230dj[]]]]]]]]]]4a02eg[0d33bj[]]1904dl[2107fe[1a13dl[4f20di[0721ee[]]]]1109on[0e0ffe[2210no[8412bi[1e19qr[]561alc[021bef[972eid[]]]]0a2ald[]]]]]]';
    // this.mode.set(TactaMode.Play);

    if (this.connectors.dbg != null) {
      GLOBALS.isDebug = true;
      src = `0000--[${this.connectors.dbg.card}01ff${this.connectors.dbg.key}[]]`;
      this.boardMode.set(BoardMode.Play);
    }
    const cvs: TactaCanvas[] = [];
    this.players = [];
    TactaCanvas.fromString(src, cvs, this);
    this.board = cvs;
  }

  playerFromCardIdx(idx: number) {
    if (idx < 1 || idx > 18 * 6) {
      return 255;
    }
    return Math.floor((idx - 1) / 18);
  }

  initBoard() {
    const cvs: TactaCanvas[] = [];
    TactaCanvas.fromString('000000--[]', cvs, this);
    for (const player of this.players) {
      player.cards = Utils.randomNumbers(18, (player.color - 1) * 18 + 1);
      player.quick = [
        {suite: 0, score: -1, active: true},
        {suite: 1, score: -1, active: false},
        {suite: 2, score: -1, active: false}
      ];
    }
    this.board = cvs;
    this.playerIdx = 0;
  }

  createPlayer(name?: string, color = -1, cards: number[] = null) {
    this.players ??= [];
    if (name == null) {
      name = $localize`Spieler ${this.players.length + 1}`;
      if (this.players.some(p => p.name === name)) {
        let idx = 2;
        do {
          name = $localize`Spieler ${idx++}`;
        } while (this.players.some(p => p.name === name));
      }
    }
    let start: number;
    const colors = [];
    if (cards == null) {
      if (color <= 0 || this.players.some(p => p.color === color)) {
        // collect colors not given to any player
        for (let i = 1; i < 7; i++) {
          if (!this.players.some(p => p.color === i)) {
            colors.push(i - 1);
          }
        }
        // pick one color
        start = colors[Math.floor(Math.random() * colors.length)] * 18 + 1;
      } else {
        start = (color - 1) * 18 + 1;
      }
    }
    const player = new Player();
    player.name = name;
    player.cards = cards ?? Utils.randomNumbers(18, start);
    player.color = player.cards?.length > 0 ? this.cardColor(player.cards[0]) : color;
    player.playerIdx = this.players.length;
    this.players.push(player);
  }

  //
  clearMarkedAreas() {
    // for (const cvs of this.cvsList) {
    //   cvs.markedAreas = '';
    // }
    for (const player of this.players) {
      for (const key of Object.keys(player.cvs)) {
        player.cvs[+key].markedAreas = '';
      }
    }
    this.markedCanvas.set(null);
  }

  adjustDimensions(cvs: TactaCanvas, values: any) {
    cvs.cardWidth = values.cardWidth;
    cvs.cardHeight = values.cardHeight;
    cvs.cardBorder = values.cardBorder;
    for (const card of cvs.cards) {
      this.adjustDimensions(card, values);
    }
  }

  // highestTurn(cvs: TactaCanvas, found?: TactaCanvas) {
  //   let max = found?.turnIdx ?? 0;
  //   if (cvs.turnIdx >= max) {
  //     found = cvs;
  //     max = found.turnIdx;
  //   }
  //   for (const card of cvs.cards) {
  //     const temp = this.highestTurn(card, found);
  //     if (temp?.turnIdx > max) {
  //       found = temp;
  //       max = found.turnIdx;
  //     }
  //   }
  //   return found;
  // }

  removeCard(cvs: TactaCanvas, cardIdx: number) {
    for (let i = 0; i < cvs.cards.length; i++) {
      if (this.normalizeCardIdx(cvs.cards[i].cardIdx) === this.normalizeCardIdx(cardIdx)) {
        cvs.cards.splice(i, 1);
      } else {
        this.removeCard(cvs.cards[i], cardIdx);
      }
    }
    this.calcScore(cvs);
  }

  cardColor(cardIdx: number) {
    return Math.floor((this.normalizeCardIdx(cardIdx) - 1) / 18) + 1;
  }

  playerIdxForCard(cardIdx: number) {
    const color = this.cardColor(cardIdx);
    return this.players.findIndex(p => p.color === color);
  }

  normalizeCardIdx(cardIdx: number) {
    return cardIdx > 127 && cardIdx < TactaService.suiteIdx ? cardIdx - 128 : cardIdx;
  }

  calcScore(cvs: TactaCanvas, init = true) {
    if (init) {
      for (const player of this.players) {
        player.score = 0;
      }
    }
    const cardIdx = this.normalizeCardIdx(cvs.cardIdx);
    for (let i = 0; i < this.deck[cardIdx].areas.length; i += 2) {
      const id = this.deck[cardIdx].areas.substring(i, i + 1);
      const score = +(this.deck[cardIdx].areas.substring(i + 1, i + 2));
      if (score > 0) {
        if (!cvs.cards.some(c => c.parentLink.endsWith(id))) {
          const idx = this.playerIdxForCard(cardIdx);
          if (idx >= 0) {
            this.players[idx].score += score;
          }
        }
      }
    }
    for (const card of cvs.cards) {
      this.calcScore(card, false);
    }
  }

  removeCardFromPlayers(cardIdx: number) {
    for (const player of this.players ?? []) {
      player.cards = player.cards.filter(c => this.normalizeCardIdx(c) !== this.normalizeCardIdx(cardIdx));
      player.cvs = [];
    }
  }

  highestTurn(cvsList?: TactaCanvas[], found?: TactaCanvas) {
    let max = found?.turnIdx ?? 0;
    cvsList ??= this.board;
    for (const cvs of cvsList) {
      if (cvs.turnIdx >= max) {
        found = cvs;
        max = found.turnIdx;
      }
      const temp = this.highestTurn(cvs.cards, found);
      if (temp?.turnIdx > max) {
        found = temp;
        max = found.turnIdx;
      }
    }
    return found;
  }

  activateNextPlayer() {
    const cvs = this.highestTurn();
    if (cvs != null) {
      this.playerIdx = (cvs.playerIdx + 1) % this.players.length;
//      this.playerIdx = (this.playerIdxForCard(cvs.cardIdx) + 1) % this.players.length;
    }
  }

  clearBoard(width?: number, height?: number) {
    this.boardWidth = width ?? this.boardWidth;
    this.boardHeight = height ?? this.boardHeight;
    if (this.boardPattern != null) {
      this.boardCanvas().ctx.fillStyle = this.boardPattern;
    } else {
      this.boardCanvas().ctx.fillStyle = 'aquamarine';
    }
    this.boardCanvas().ctx.fillRect(0, 0, this.boardWidth, this.boardHeight);
    this.boardCanvas().ctx.fill();
  }
}
